import { Component, OnInit, OnDestroy, HostBinding, HostListener, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { TabStateService } from '../../core/services/tab-state.service';
import { SignMechanismService, SignMechanism } from '../../core/services/sign-mechanism.service';
import { SignStateService } from '../../core/services/sign-state.service';
import { TiposDocumentoService } from '../../core/services/tipos-documento.service';
import { AgentAvailabilityService } from '../../core/services/agent-availability.service';
import { TourService, TourId } from '../../core/services/tour.service';
import { resolveTourIdFromUrl } from '../../core/tour/tour.definitions';
import { ADMIN_ROUTES, BRAND, CERTIFIRMA, CERTIFIRMA_DESC } from '../../core/constants/nomenclature.constants';

type MechanismItem = {
  id: SignMechanism;
  label: string;
  desc: string;
  iconClass: string;
  iconWrapClass: string;
};

@Component({
  selector: 'app-app-shell',
  templateUrl: './app-shell.component.html',
  styleUrls: ['./app-shell.component.css'],
  standalone: false
})
export class AppShellComponent implements OnInit, OnDestroy {
  @HostBinding('class.drawer-open') sidebarOpen = false;

  /** Menús expandibles del sidebar (acordeón). */
  firmarVerificarMenuOpen = true;
  adminMenuOpen = false;
  gestionMenuOpen = false;
  /** Temporal: false oculta el menú Gestión del sidebar (pruebas). Volver a true para reactivar. */
  readonly showGestionMenu = true;

  private destroy$ = new Subject<void>();
  private autoTourScheduledFor: string | null = null;
  /** Viewport ≤768px: el sidebar actúa como drawer. */
  mobileDrawerMode = false;
  private wasMobileViewport: boolean | null = null;
  isVerifyRoute = false;
  isFirmarRoute = false;
  isAdminTokenVirtualRoute = false;
  isAdminTokenVirtualRseRoute = false;
  isAdminP10Route = false;
  isAdminTokenFisicoRoute = false;
  isDescargarCertificadoRoute = false;
  isGestionEstadoRoute = false;
  isGestionFacturaRoute = false;
  isGestionRevocarRoute = false;
  currentMechanism: SignMechanism = 'rse';
  /** True solo si el agente local responde healthy. */
  agentAvailable = false;
  readonly disableVerifyInPhaseOne = false;
  readonly adminTokenVirtualEnabled = true;
  readonly adminTokenVirtualRseEnabled = true;
  readonly adminP10Enabled = true;

  private readonly baseMechanisms: MechanismItem[] = [
    {
      id: 'rse',
      label: CERTIFIRMA.VIRTUAL_OTP,
      desc: CERTIFIRMA_DESC.VIRTUAL_OTP,
      iconClass: 'fas fa-shield-halved',
      iconWrapClass: 'icon-rse'
    },
    {
      id: 'token-virtual',
      label: CERTIFIRMA.VIRTUAL,
      desc: CERTIFIRMA_DESC.VIRTUAL,
      iconClass: 'fas fa-cloud',
      iconWrapClass: 'icon-token-virtual'
    },
    {
      id: 'p10',
      label: CERTIFIRMA.PKCS10,
      desc: CERTIFIRMA_DESC.PKCS10,
      iconClass: 'fas fa-file-lines',
      iconWrapClass: 'icon-p10'
    }
  ];

  private readonly tokenFisicoMechanism: MechanismItem = {
    id: 'token-fisico',
    label: CERTIFIRMA.FISICO,
    desc: CERTIFIRMA_DESC.FISICO,
    iconClass: 'fas fa-usb',
    iconWrapClass: 'icon-token-fisico'
  };

  mechanisms: MechanismItem[] = [...this.baseMechanisms];

  readonly brand = BRAND;
  readonly certifirma = CERTIFIRMA;
  readonly certifirmaDesc = CERTIFIRMA_DESC;

  canalVentasUrl = environment.canalVentas;
  certicamaraUrl = 'https://web.certicamara.com';
  appVersion = environment.version;
  currentYear = new Date().getFullYear();

  constructor(
    private router: Router,
    private tabStateService: TabStateService,
    public signMechanismService: SignMechanismService,
    private signStateService: SignStateService,
    private tourService: TourService,
    private tiposDocumento: TiposDocumentoService,
    private agentAvailability: AgentAvailabilityService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /** Feature flag: Certifirma Digital Físico + health del agente local. */
  get tokenFisicoEnabled(): boolean {
    return !!environment.tokenFisicoEnabled;
  }

  ngOnInit(): void {
    // Catálogo de tipos de identificación (compra/SSPS); fallback si el servicio falla.
    this.tiposDocumento.ensureLoaded().pipe(takeUntil(this.destroy$)).subscribe();
    if (this.tokenFisicoEnabled) {
      this.agentAvailability.startWatching();
      this.agentAvailability.isAvailable$.pipe(takeUntil(this.destroy$)).subscribe((ok) => {
        this.onAgentAvailabilityChange(ok);
      });
    } else {
      this.onAgentAvailabilityChange(false);
    }
    this.updateFromUrl(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(e => {
        // En móvil el drawer debe cerrarse al cambiar de ruta
        this.closeSidebar();
        this.updateFromUrl(e.urlAfterRedirects || e.url);
      });
    this.signMechanismService.value$.pipe(takeUntil(this.destroy$)).subscribe(m => {
      this.currentMechanism = m;
      this.syncCertificateType(m);
    });
    this.syncDrawerForViewport();
  }

  ngOnDestroy(): void {
    this.sidebarOpen = false;
    this.updateBodyScroll();
    this.agentAvailability.stopWatching();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Tour contextual según la ruta actual; fuerza apertura desde el header. */
  openContextualTour(): void {
    const tourId = this.currentTourId();
    this.tourService.openTourForced(tourId ?? 'sign');
  }

  get tourButtonLabel(): string {
    const id = this.currentTourId();
    if (id === 'verify') return 'Guía de verificación';
    if (id === 'admin-tv' || id === 'admin-rse') return 'Guía de administración';
    if (id === 'sign') return 'Guía para firmar';
    return 'Ver guía';
  }

  get isAnyAdminRoute(): boolean {
    return this.isAdminTokenVirtualRoute
      || this.isAdminTokenVirtualRseRoute
      || this.isAdminP10Route
      || this.isAdminTokenFisicoRoute
      || this.isDescargarCertificadoRoute;
  }

  get isAnyGestionRoute(): boolean {
    return this.isGestionEstadoRoute
      || this.isGestionFacturaRoute
      || this.isGestionRevocarRoute;
  }

  private currentTourId(): TourId | null {
    return resolveTourIdFromUrl(this.router.url);
  }

  private updateFromUrl(url: string): void {
    this.isVerifyRoute = url.includes('verificar');
    this.isFirmarRoute = url.includes('firmar');
    this.isAdminTokenVirtualRseRoute =
      url.includes('administrar/certifirma-virtual-otp')
      || url.includes('administrar/token-virtual-codigo')
      || url.includes('administrar-token-virtual-rse');
    this.isAdminTokenVirtualRoute =
      (url.includes('administrar/certifirma-virtual') && !this.isAdminTokenVirtualRseRoute)
      || (url.includes('administrar/token-virtual') && !this.isAdminTokenVirtualRseRoute)
      || (url.includes('administrar-token-virtual') && !url.includes('administrar-token-virtual-rse'));
    this.isAdminP10Route =
      url.includes('administrar/certifirma-pkcs10') || url.includes('administrar/p10');
    this.isAdminTokenFisicoRoute =
      url.includes('administrar/certifirma-fisico')
      || url.includes('administrar/token-fisico')
      || (url.includes('administrar-token') && !url.includes('administrar-token-virtual'));
    this.isDescargarCertificadoRoute = url.includes('descargar-certificado');
    this.isGestionEstadoRoute = url.includes('gestionar/estado-tramite');
    this.isGestionFacturaRoute = url.includes('gestionar/factura');
    this.isGestionRevocarRoute = url.includes('gestionar/revocar');

    // Rutas de Certifirma Digital Físico solo si el feature está activo y el agente responde
    if (
      (!this.tokenFisicoEnabled || !this.agentAvailable) &&
      (this.isAdminTokenFisicoRoute || this.isDescargarCertificadoRoute)
    ) {
      void this.router.navigate(['/firmar']);
    }

    if (this.isAnyAdminRoute) {
      this.adminMenuOpen = true;
      this.gestionMenuOpen = false;
      this.firmarVerificarMenuOpen = false;
    } else if (this.isAnyGestionRoute) {
      this.gestionMenuOpen = true;
      this.adminMenuOpen = false;
      this.firmarVerificarMenuOpen = false;
    } else if (this.isFirmarRoute || this.isVerifyRoute) {
      this.firmarVerificarMenuOpen = true;
      this.adminMenuOpen = false;
      this.gestionMenuOpen = false;
    }

    this.scheduleAutoTour(url);
  }

  private scheduleAutoTour(url: string): void {
    const tourId = resolveTourIdFromUrl(url);
    if (!tourId) return;
    // En celular/tablet estrecha no abrir automáticamente: solo el botón Guía.
    if (typeof window !== 'undefined' && window.innerWidth <= 768) return;
    if (this.autoTourScheduledFor === tourId) return;
    this.autoTourScheduledFor = tourId;
    setTimeout(() => this.tourService.openTour(tourId), 550);
  }

  private syncCertificateType(mechanism: SignMechanism): void {
    if (mechanism === 'rse' || mechanism === 'token-virtual' || mechanism === 'token-fisico') {
      this.signStateService.setCertificateType('token');
    } else if (mechanism === 'p10') {
      this.signStateService.setCertificateType('p10');
    }
  }

  private onAgentAvailabilityChange(ok: boolean): void {
    this.agentAvailable = ok;
    this.mechanisms = ok
      ? [...this.baseMechanisms, this.tokenFisicoMechanism]
      : [...this.baseMechanisms];

    if (!ok && this.currentMechanism === 'token-fisico') {
      this.signMechanismService.setMechanism('rse');
    }

    if (!ok && (this.isAdminTokenFisicoRoute || this.isDescargarCertificadoRoute)) {
      void this.router.navigate(['/firmar']);
    }
  }

  selectMechanism(mechanism: SignMechanism): void {
    this.closeSidebar();
    this.signMechanismService.setMechanism(mechanism);
    this.tabStateService.setActiveTab('sign');
    this.autoTourScheduledFor = null;
    this.router.navigate(['/firmar']);
  }

  goToVerificar(): void {
    if (this.disableVerifyInPhaseOne) return;
    this.closeSidebar();
    this.tabStateService.setActiveTab('verify');
    this.autoTourScheduledFor = null;
    this.router.navigate(['/verificar']);
  }

  goToAdministrarTokenVirtual(): void {
    if (!this.adminTokenVirtualEnabled) return;
    this.closeSidebar();
    this.autoTourScheduledFor = null;
    this.router.navigate([ADMIN_ROUTES.VIRTUAL]);
  }

  goToAdministrarTokenVirtualRse(): void {
    if (!this.adminTokenVirtualRseEnabled) return;
    this.closeSidebar();
    this.autoTourScheduledFor = null;
    this.router.navigate([ADMIN_ROUTES.VIRTUAL_OTP]);
  }

  goToAdministrarP10(): void {
    if (!this.adminP10Enabled) return;
    this.closeSidebar();
    this.autoTourScheduledFor = null;
    this.router.navigate([ADMIN_ROUTES.PKCS10]);
  }

  goToAdministrarTokenFisico(): void {
    if (!this.agentAvailable) return;
    this.closeSidebar();
    this.autoTourScheduledFor = null;
    void this.router.navigate([ADMIN_ROUTES.FISICO]);
  }

  goToDescargarCertificado(): void {
    if (!this.agentAvailable) return;
    this.closeSidebar();
    this.autoTourScheduledFor = null;
    void this.router.navigate([ADMIN_ROUTES.DESCARGAR_CERTIFICADO]);
  }

  goToGestion(tab: 'estado-tramite' | 'factura' | 'revocar'): void {
    this.closeSidebar();
    this.autoTourScheduledFor = null;
    void this.router.navigate(['/gestionar', tab]);
  }

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
    this.updateBodyScroll();
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
    this.updateBodyScroll();
  }

  toggleFirmarVerificar(): void {
    this.firmarVerificarMenuOpen = !this.firmarVerificarMenuOpen;
    if (this.firmarVerificarMenuOpen) {
      this.adminMenuOpen = false;
      this.gestionMenuOpen = false;
    }
  }

  toggleAdmin(): void {
    this.adminMenuOpen = !this.adminMenuOpen;
    if (this.adminMenuOpen) {
      this.firmarVerificarMenuOpen = false;
      this.gestionMenuOpen = false;
    }
  }

  toggleGestion(): void {
    this.gestionMenuOpen = !this.gestionMenuOpen;
    if (this.gestionMenuOpen) {
      this.firmarVerificarMenuOpen = false;
      this.adminMenuOpen = false;
    }
  }

  private updateBodyScroll(): void {
    this.document.body.classList.toggle('drawer-open', this.sidebarOpen);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeSidebar();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.syncDrawerForViewport();
  }

  /**
   * Sincroniza el modo drawer con el viewport.
   * Solo fuerza cierre al cruzar el breakpoint (o en la primera evaluación móvil),
   * no en cada resize menor (p. ej. barra del navegador).
   */
  private syncDrawerForViewport(): void {
    if (typeof window === 'undefined') return;
    const isMobile = window.innerWidth <= 768;
    this.mobileDrawerMode = isMobile;

    if (this.wasMobileViewport === null) {
      this.wasMobileViewport = isMobile;
      if (isMobile) {
        this.closeSidebar();
      }
      return;
    }

    if (isMobile !== this.wasMobileViewport) {
      this.wasMobileViewport = isMobile;
      this.closeSidebar();
    }
  }

  goToCanalVentas(): void {
    window.open(this.canalVentasUrl, '_blank');
  }
}
